/**
 * DealerPillNav.tsx — bottom nav mobile del concessionario (Priority+).
 *
 * Estratto da Navbar.tsx (28 Ott 2026): contiene solo stato locale del
 * bottom sheet e link; nessuna dipendenza da WebSocket o store globali.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bell,
  Bike,
  Calendar,
  Columns,
  CreditCard,
  Eye,
  LogOut,
  MoreHorizontal,
  Store,
  TrendingUp,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import useIsActive from "@/components/nav/useIsActive";

interface DealerPillNavProps {
  dmUnread: number;
  totalNotifUnread: number;
  chapterRequests: number;
  affiliationRequests: number;
  onLogout: () => void;
}

export default function DealerPillNav({
  dmUnread,
  totalNotifUnread,
  chapterRequests,
  affiliationRequests,
  onLogout,
}: DealerPillNavProps) {
  const isActive = useIsActive();
  const [showOverflow, setShowOverflow] = useState(false);

  function PillItem({
    to,
    icon,
    label,
    badge,
    onClick,
  }: {
    to?: string;
    icon: React.ReactNode;
    label: string;
    badge?: number;
    onClick?: () => void;
  }) {
    const active = to ? isActive(to) : false;
    const content = (
      <div
        className={`transition-all duration-300 ${
          active
            ? "text-brand-500 scale-110"
            : "text-neutral-500 group-hover:text-neutral-300"
        }`}
      >
        <div className="relative">
          {icon}
          {badge && badge > 0 ? (
            <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-0.5 rounded-full bg-orange-500 text-black text-[9px] font-extrabold flex items-center justify-center">
              {badge > 9 ? "9+" : badge}
            </span>
          ) : null}
        </div>
      </div>
    );

    const labelEl = (
      <span
        className={`text-[10px] font-medium transition-colors ${active ? "text-brand-500" : "text-neutral-500"}`}
      >
        {label}
      </span>
    );

    if (to) {
      return (
        <Link
          to={to}
          className="relative flex flex-col items-center justify-center gap-1 px-2 py-1 group"
          aria-label={label}
        >
          {content}
          {labelEl}
        </Link>
      );
    }
    return (
      <button
        onClick={onClick}
        className="relative flex flex-col items-center justify-center gap-1 px-2 py-1 group"
        aria-label={label}
      >
        {content}
        {labelEl}
      </button>
    );
  }

  const overflowBadge = (totalNotifUnread || 0) + (affiliationRequests || 0);

  return (
    <>
      <div
        className="mobile-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-50"
        style={{
          paddingBottom:
            "env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px))",
        }}
      >
        <nav className="bg-black/90 backdrop-blur-3xl border-t border-white/10 px-2 py-2 flex justify-around items-center">
          <PillItem
            to="/dealer/dashboard"
            icon={<BarChart3 size={22} />}
            label="Dashboard"
          />
          <PillItem
            to="/dealer/leads"
            icon={<Columns size={22} />}
            label="Pipeline"
          />
          <PillItem
            to="/dealer/chapters"
            icon={<Users size={22} />}
            label="Chapters"
            badge={chapterRequests}
          />
          <PillItem
            to="/dealer/affiliations"
            icon={<UserCheck size={22} />}
            label="Affiliati"
            badge={affiliationRequests}
          />
          <PillItem
            icon={<MoreHorizontal size={22} />}
            label="Altro"
            badge={overflowBadge}
            onClick={() => setShowOverflow(true)}
          />
        </nav>
      </div>

      {/* Overflow Bottom Sheet */}
      {showOverflow && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowOverflow(false)}
          />
          <div
            className="absolute bottom-0 left-0 right-0 bg-neutral-950 border-t border-white/10 rounded-t-2xl p-4 pb-8 animate-spring-up"
            style={{
              paddingBottom:
                "calc(1rem + env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px)))",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="text-white font-bold text-lg">
                Menu Concessionario
              </h3>
              <button
                onClick={() => setShowOverflow(false)}
                className="p-1 text-neutral-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Griglia organizzata: 3 colonne con voci raggruppate */}
            <div className="mb-3 px-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">
                Business
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <OverflowItem
                to="/dealer/analytics"
                icon={<Activity size={24} />}
                label="Analytics"
              />
            </div>

            <div className="mb-3 px-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-green-400">
                Vendita & Clienti
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <OverflowItem
                to="/dealer/appointments"
                icon={<Calendar size={24} />}
                label="Appuntam."
              />
              <OverflowItem
                to="/dealer/promotions"
                icon={<TrendingUp size={24} />}
                label="Promo"
              />
              <OverflowItem
                to="/dealer/followups"
                icon={<Bell size={24} />}
                label="Follow-up"
              />
            </div>

            <div className="mb-3 px-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-orange-400">
                Esposizione
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <OverflowItem
                to="/dealer/showcase"
                icon={<Store size={24} />}
                label="Vetrina"
              />
              <OverflowItem
                to="/dealer/rental"
                icon={<Bike size={24} />}
                label="Noleggio"
              />
              <OverflowItem
                to="/dealer/nearby"
                icon={<Eye size={24} />}
                label="Visibilità"
              />
            </div>

            <div className="mb-3 px-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400">
                Community
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <OverflowItem
                to="/dealer/affiliations"
                icon={<UserCheck size={24} />}
                label="Affiliati"
                badge={affiliationRequests}
              />
              <OverflowItem
                to="/dealer/events"
                icon={<Calendar size={24} />}
                label="Eventi"
              />
            </div>

            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/[0.20]">
              {/* FIX DEALER-MENU (Set 2026): Profilo & Anagrafica unificate +
                  'Il mio Piano' sotto, come nel menu desktop. */}
              <OverflowItem
                to="/dealer/profile"
                icon={<Store size={24} />}
                label="Profilo & Anagr."
              />
              <OverflowItem
                to="/dealer/subscription"
                icon={<CreditCard size={24} />}
                label="Il mio Piano"
              />
              <button
                onClick={() => {
                  setShowOverflow(false);
                  onLogout();
                }}
                className="flex flex-col items-center gap-1 p-2 rounded-xl text-red-400 hover:bg-red-500/10 active:scale-95 transition-all"
                aria-label="Esci"
              >
                <LogOut size={24} />
                <span className="text-[10px] font-medium">Esci</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function OverflowItem({
  to,
  icon,
  label,
  badge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-1 p-2 rounded-xl text-neutral-400 hover:text-white hover:bg-white/5 active:scale-95 transition-all"
      aria-label={label}
    >
      <div className="relative">
        {icon}
        {badge && badge > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-0.5 rounded-full bg-orange-500 text-black text-[9px] font-extrabold flex items-center justify-center">
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </div>
      <span className="text-[10px] font-medium">{label}</span>
    </Link>
  );
}
